import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Lock, Crown, Zap } from "lucide-react";
import { startGoogleLogin } from "@/lib/googleAuth";

export function PremiumLock({ 
  feature = "this feature",
  description = "Upgrade to Pro to unlock this premium feature.",
  showCard = true 
}) {
  const navigate = useNavigate();

  const content = (
    <div className="text-center py-8">
      <div className="w-20 h-20 bg-gradient-to-br from-violet-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Lock className="w-10 h-10 text-violet-600" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">
        Premium Feature
      </h3>
      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        {description}
      </p>
      
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button 
          onClick={() => navigate("/pricing")}
          className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
          data-testid="upgrade-btn"
        >
          <Crown className="w-4 h-4 mr-2" />
          Upgrade to Pro
        </Button>
        <Button 
          variant="outline"
          onClick={() => navigate("/pricing")}
          data-testid="view-plans-btn"
        >
          View All Plans
        </Button>
      </div>

      <div className="mt-6 p-4 bg-violet-50 rounded-lg max-w-md mx-auto">
        <div className="flex items-center gap-2 text-sm text-violet-700">
          <Zap className="w-4 h-4" />
          <span>Pro plan starts at just ₹499/month</span>
        </div>
      </div>
    </div>
  );

  if (showCard) {
    return <Card className="p-6">{content}</Card>;
  }

  return content;
}

export function PremiumOverlay({ children, isLocked, feature }) {
  const navigate = useNavigate();

  if (!isLocked) {
    return children;
  }

  return (
    <div className="relative">
      <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
        <div className="text-center p-6">
          <Lock className="w-12 h-12 text-violet-600 mx-auto mb-3" />
          <p className="font-semibold text-gray-900 mb-2">Premium Feature</p>
          <p className="text-sm text-gray-600 mb-4">
            Upgrade to unlock {feature}
          </p>
          <Button 
            size="sm"
            onClick={() => navigate("/pricing")}
            className="bg-violet-600 hover:bg-violet-700"
          >
            <Crown className="w-3 h-3 mr-1" />
            Upgrade
          </Button>
        </div>
      </div>
      <div className="opacity-30 pointer-events-none">
        {children}
      </div>
    </div>
  );
}

export function LoginRequired({ message = "Please login to access this feature." }) {
  const navigate = useNavigate();

  return (
    <Card className="p-6">
      <div className="text-center py-8">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Lock className="w-10 h-10 text-gray-400" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Login Required
        </h3>
        <p className="text-gray-600 mb-6">
          {message}
        </p>
        <Button 
          onClick={() => { startGoogleLogin(); }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          Sign in with Google
        </Button>
      </div>
    </Card>
  );
}

export default PremiumLock;
